import { useEffect, useState } from 'react';
// ID-12905 spike — throwaway
import { SpikeNavDemo } from 'shared/spike';
import { SPIKE_SHARED_PORTAL_PATH } from '@/constants';
import { useHistory } from 'react-router-dom';
import { NavModuleProps, getLuminance, getCSSVariable } from 'vopay';
import { useTranslation } from 'react-i18next';
import { useModuleFeatureFlag } from '../providers/FeatureFlag.provider';
import { useClientData } from '../providers/ClientData.provider';
import { DEFAULT_MODULE } from '../constants';
import { LockedFeaturesCtx, useLockedFeatures } from '../providers/LockedFeatures.provider';
import { useLockedFeatureDialogue } from '../providers/LockedFeatureDialogue.provider';
import { useApprovalWorkflow } from '../providers/ApprovalWorkflow.provider';

type SubNavModule = {
  onNavClick: (currModule: string) => void;
  onSubNavClick: (moduleId: string, destination: string, currLabel: string, isLocked?: boolean) => void;
  goBackInNavLeft: () => void;
  getSubNavModules: (parentModule?: string) => NavModuleProps[];
  setSubNavActiveModule: (activeModule: string) => void;
  reset: () => void;
  parentModules: string[];
  label: string;
  description: string;
  subNavLabel: string;
  hasParent: boolean;
};

const useSubModule = (): SubNavModule => {
  const history = useHistory();
  const [parentModules, setParentModules] = useState<string[]>([]);
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [hasParent, setHasParent] = useState(false);
  const [subNavActiveModule, setSubNavActiveModule] = useState('');
  const [subNavLabel, setSubNavLabel] = useState('');
  const dashboardFeatureFlags = useModuleFeatureFlag('dashboard');
  const myAccountFeatureFlags = useModuleFeatureFlag('myAccount');
  const subAccountsFeatureFlags = useModuleFeatureFlag('subAccounts');
  const clientAccountsFeatureFlags = useModuleFeatureFlag('clientAccounts');
  const paymentFeatureFlags = useModuleFeatureFlag('payment');
  const collectionFeatureFlags = useModuleFeatureFlag('collection');
  const conversionFeatureFlags = useModuleFeatureFlag('conversion');
  const contactsFeatureFlags = useModuleFeatureFlag('contacts');
  const uploadFeatureFlags = useModuleFeatureFlag('upload');
  const softwareFeatureFlags = useModuleFeatureFlag('software');
  const transactionsFeatureFlags = useModuleFeatureFlag('transactions');
  const statementsFeatureFlags = useModuleFeatureFlag('statements');
  const integrationsFeatureFlags = useModuleFeatureFlag('integrations');
  const settingsFeatureFlags = useModuleFeatureFlag('settings');
  const supportFeatureFlags = useModuleFeatureFlag('support');
  const transactionApprovalFeatureFlags = useModuleFeatureFlag('transactionApproval');
  const globalSendFeatureFlags = useModuleFeatureFlag('globalSend');
  const disputeManagementFeatureFlags = useModuleFeatureFlag('disputeManagement');
  const elinxFeatureFlags = useModuleFeatureFlag('eLinx');
  const lockedFeatures = useLockedFeatures();
  const { updateIsLocked } = useLockedFeatureDialogue();
  const { hasTransactionsRequiringApproval, hasInvalidApprover, hasInvalidWorkflows } = useApprovalWorkflow();

  const secondaryColor = getCSSVariable('--secondaryColor');

  useEffect(() => {
    if (parentModules.length === 0) setHasParent(false);
  }, [parentModules]);

  function onNavClick(currModule: string) {
    const [modLabel, modDescription] = getLabelAndDescription(currModule);
    setParentModules((prevState) => [...prevState, currModule]);

    if (currModule === 'dashboard') {
      setSubNavActiveModule('');
    }

    setLabel(modLabel || '');
    setDescription(modDescription || '');
    setHasParent(true);
  }

  function onSubNavClick(moduleId: string, destination: string, currLabel: string, isLocked = false) {
    if (isLocked) {
      updateIsLocked(true, currLabel);
    } else if (history.location.pathname !== destination) {
      history.push(destination);
    } else {
      setSubNavActiveModule(moduleId);
      setSubNavLabel(currLabel);
    }
  }

  function goBackInNavLeft() {
    setParentModules((prevState) => prevState.slice(0, prevState.length - 1));
  }

  function reset() {
    setParentModules([]);
    setLabel('');
    setDescription('');
    setHasParent(false);
    setSubNavActiveModule('');
    setSubNavLabel('');
  }

  function getSubNavModules(parentModule?: string) {
    return getModuleHandler(
      parentModule || DEFAULT_MODULE,
      subNavActiveModule,
      {
        ...dashboardFeatureFlags,
        ...myAccountFeatureFlags,
        ...subAccountsFeatureFlags,
        ...clientAccountsFeatureFlags,
        ...paymentFeatureFlags,
        ...collectionFeatureFlags,
        ...conversionFeatureFlags,
        ...contactsFeatureFlags,
        ...uploadFeatureFlags,
        ...softwareFeatureFlags,
        ...transactionsFeatureFlags,
        ...statementsFeatureFlags,
        ...integrationsFeatureFlags,
        ...settingsFeatureFlags,
        ...supportFeatureFlags,
        ...transactionApprovalFeatureFlags,
        ...globalSendFeatureFlags,
        ...disputeManagementFeatureFlags,
        ...elinxFeatureFlags,
      },
      lockedFeatures,
      {
        hasTransactionsRequiringApproval,
        hasInvalidApprover,
        hasInvalidWorkflows,
      },
      secondaryColor,
      onSubNavClick,
    );
  }

  return {
    onNavClick,
    onSubNavClick,
    goBackInNavLeft,
    getSubNavModules,
    reset,
    setSubNavActiveModule,
    parentModules,
    label,
    description,
    subNavLabel,
    hasParent,
  };
};

export default useSubModule;

function getModuleHandler(
  parentModule: string,
  activeModule: string,
  featureFlags: { [feature: string]: boolean | undefined },
  lockedSubModule: LockedFeaturesCtx,
  approvalInformation: {
    hasTransactionsRequiringApproval: boolean;
    hasInvalidApprover: boolean;
    hasInvalidWorkflows: boolean;
  },
  secondaryColor: string,
  callback: (moduleId: string, destination: string, currLabel: string, isLocked?: boolean) => void,
) {
  const {
    response: { AccountType, ClientAccountsEnabled },
  } = useClientData();

  const { t } = useTranslation('translations', { keyPrefix: 'useSubModule' });

  const handlers: { [key: string]: NavModuleProps[] } = {
    dashboard: [],
    myAccount: [
      {
        id: 'navModule-accountSummary',
        icon: 'summary' as const,
        activeIcon: 'summaryOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('accountSummary.label'),
        isActive: activeModule === 'accountSummary',
        subNavModules: [],
        isOpen: false,
        path: '/account-summary',
        title: t('accountSummary.title'),
        activeModuleId: 'accountSummary',
        onClick: () => callback('accountSummary', '/account-summary', t('accountSummary.title')),
        showNavModule: () => Boolean(featureFlags.AccountSummary),
        isLocked: () => false,
      },
      // ID-12905 spike — dev-only, throwaway. Nav entry for the shared-component spike route.
      {
        id: 'navModule-spikeSharedPortal',
        icon: 'accountBilling' as const,
        activeIcon: 'accountBillingOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: 'Spike: shared portal',
        isActive: activeModule === 'spikeSharedPortal',
        subNavModules: [],
        isOpen: false,
        path: SPIKE_SHARED_PORTAL_PATH,
        title: 'Spike: shared portal',
        activeModuleId: 'spikeSharedPortal',
        onClick: () => callback('spikeSharedPortal', SPIKE_SHARED_PORTAL_PATH, 'Spike: shared portal'),
        showNavModule: () => import.meta.env.DEV,
        isLocked: () => false,
      },
      // ID-12905 spike — the shared-owned sub-page;
      {
        id: `navModule-${SpikeNavDemo.routes.subPage.id}`,
        icon: 'accountBilling' as const,
        activeIcon: 'accountBillingOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('spikeSubPageLabel', { defaultValue: 'Spike: sub-page' }),
        isActive: activeModule === SpikeNavDemo.routes.subPage.id,
        subNavModules: [],
        isOpen: false,
        path: SpikeNavDemo.routes.subPage.getPath({ basePath: SPIKE_SHARED_PORTAL_PATH }),
        title: t('spikeSubPageLabel', { defaultValue: 'Spike: sub-page' }),
        activeModuleId: SpikeNavDemo.routes.subPage.id,
        onClick: () =>
          callback(
            SpikeNavDemo.routes.subPage.id,
            SpikeNavDemo.routes.subPage.getPath({ basePath: SPIKE_SHARED_PORTAL_PATH }),
            t('spikeSubPageLabel', { defaultValue: 'Spike: sub-page' }),
          ),
        showNavModule: () => import.meta.env.DEV,
        isLocked: () => false,
      },
      {
        id: 'navModule-accountAndBilling',
        icon: 'accountBilling' as const,
        activeIcon: 'accountBillingOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('accountAndBilling.label'),
        isActive: activeModule === 'accountAndBilling',
        subNavModules: [],
        isOpen: false,
        path: '/account-and-billing',
        title: t('accountAndBilling.title'),
        activeModuleId: 'accountAndBilling',
        onClick: () =>
          callback(
            'accountAndBilling',
            '/account-and-billing',
            t('accountAndBilling.title'),
            !lockedSubModule.AccountAndBillingEnabled,
          ),
        showNavModule: () => Boolean(featureFlags.ViewAccountAndBilling),
        isLocked: () => !lockedSubModule.AccountAndBillingEnabled,
      },
      {
        id: 'navModule-fundMyAccount',
        icon: 'fund' as const,
        activeIcon: 'fundOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('fundMyAccount.label'),
        path: '/fund-my-account',
        title: t('fundMyAccount.title'),
        activeModuleId: 'fundMyAccount',
        isActive: activeModule === 'fundMyAccount',
        isOpen: false,
        subNavModules: [],
        onClick: () =>
          callback(
            'fundMyAccount',
            '/fund-my-account',
            t('fundMyAccount.title'),
            !lockedSubModule.FundMyAccountEnabled,
          ),
        showNavModule: () => Boolean(featureFlags.FundMyAccount),
        isLocked: () => !lockedSubModule.FundMyAccountEnabled,
      },
      {
        id: 'navModule-withdrawMyAccount',
        icon: 'withdraw' as const,
        activeIcon: 'withdrawOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('withdrawMyAccount.label'),
        isActive: activeModule === 'withdrawMyAccount',
        path: '/withdraw-my-account',
        title: t('withdrawMyAccount.title'),
        activeModuleId: 'withdrawMyAccount',
        isOpen: false,
        subNavModules: [],
        onClick: () =>
          callback(
            'withdrawMyAccount',
            '/withdraw-my-account',
            t('withdrawMyAccount.title'),
            !lockedSubModule.WithdrawMyAccountEnabled,
          ),
        showNavModule: () => Boolean(featureFlags.WithdrawMyAccount),
        isLocked: () => !lockedSubModule.WithdrawMyAccountEnabled,
      },
      {
        id: 'navModule-bankAccountTransfer',
        icon: 'bankAccountTransfer' as const,
        activeIcon: 'bankAccountTransferOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('bankAccountTransfer.label'),
        isActive: activeModule === 'bankAccountTransfer',
        path: '/bank-account-transfer',
        title: t('bankAccountTransfer.title'),
        activeModuleId: 'bankAccountTransfer',
        isOpen: false,
        subNavModules: [],
        onClick: () =>
          callback(
            'bankAccountTransfer',
            '/bank-account-transfer',
            t('bankAccountTransfer.title'),
            !lockedSubModule.BankAccountTransferEnabled,
          ),
        showNavModule: () => Boolean(featureFlags.BankAccountTransfer) && Number(ClientAccountsEnabled) === 0,
        isLocked: () => !lockedSubModule.BankAccountTransferEnabled,
      },
      {
        id: 'navModule-myBankAccounts',
        icon: 'operationalAccounts' as const,
        activeIcon: 'operationalAccountsOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('myBankAccounts.label'),
        isActive: activeModule === 'myBankAccounts',
        path: '/operational-accounts',
        title: t('myBankAccounts.title'),
        activeModuleId: 'myBankAccounts',
        isOpen: false,
        subNavModules: [],
        onClick: () =>
          callback(
            'myBankAccounts',
            '/operational-accounts',
            t('myBankAccounts.title'),
            !lockedSubModule.OperationalAccountsEnabled,
          ),
        showNavModule: () => Boolean(featureFlags.OperationalAccounts) && Number(ClientAccountsEnabled) === 0,
        isLocked: () => !lockedSubModule.OperationalAccountsEnabled,
      },
      {
        id: 'navModule-autoBalanceTransfer',
        icon: 'autoBalanceTransfer' as const,
        activeIcon: 'autoBalanceTransferOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('autoBalanceTransfer.label'),
        isActive: activeModule === 'autoBalanceTransfer',
        path: '/auto-balance-transfer',
        title: t('autoBalanceTransfer.title'),
        activeModuleId: 'autoBalanceTransfer',
        isOpen: false,
        subNavModules: [],
        onClick: () =>
          callback(
            'autoBalanceTransfer',
            '/auto-balance-transfer',
            t('autoBalanceTransfer.title'),
            !lockedSubModule.AutoBalanceTransferEnabled,
          ),
        showNavModule: () => Boolean(featureFlags.AutoBalanceTransfer),
        isLocked: () => !lockedSubModule.AutoBalanceTransferEnabled,
      },
    ].filter((subModule) => subModule.showNavModule()),
    transactionApproval: [
      {
        id: 'navModule-approvalInbox',
        icon: 'approvalInbox' as const,
        activeIcon: 'approvalInboxOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('approvalInbox.label'),
        isActive: activeModule === 'approvalInbox',
        path: '/transaction-approval/inbox',
        title: t('approvalInbox.title'),
        activeModuleId: 'approvalInbox',
        isOpen: false,
        subNavModules: [],
        onClick: () =>
          callback(
            'approvalInbox',
            '/transaction-approval/inbox',
            t('approvalInbox.title'),
            !lockedSubModule.TransactionApprovalWorkflowEnabled,
          ),
        showNavModule: () => Boolean(featureFlags.Inbox),
        isLocked: () => !lockedSubModule.TransactionApprovalWorkflowEnabled,
        hasNotification: approvalInformation.hasTransactionsRequiringApproval,
        NotificationType: 'icon' as const,
      },
      {
        id: 'navModule-approvalList',
        icon: 'approvalList' as const,
        activeIcon: 'approvalListOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('approvalList.label'),
        isActive: activeModule === 'approvalList',
        path: '/transaction-approval/approval-list',
        title: t('approvalList.title'),
        activeModuleId: 'approvalList',
        isOpen: false,
        subNavModules: [],
        onClick: () =>
          callback(
            'approvalList',
            '/transaction-approval/approval-list',
            t('approvalList.title'),
            !lockedSubModule.TransactionApprovalWorkflowEnabled,
          ),
        showNavModule: () => Boolean(featureFlags.Configuration),
        isLocked: () => !lockedSubModule.TransactionApprovalWorkflowEnabled,
        hasNotification: approvalInformation.hasInvalidApprover,
        NotificationType: 'icon' as const,
      },
      {
        id: 'navModule-approvalConfiguration',
        icon: 'approvalConfiguration' as const,
        activeIcon: 'approvalConfigurationOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('approvalConfiguration.label'),
        isActive: activeModule === 'approvalConfiguration',
        path: '/transaction-approval/configuration',
        title: t('approvalConfiguration.title'),
        activeModuleId: 'approvalConfiguration',
        isOpen: false,
        subNavModules: [],
        onClick: () =>
          callback(
            'approvalConfiguration',
            '/transaction-approval/configuration',
            t('approvalConfiguration.title'),
            !lockedSubModule.TransactionApprovalWorkflowEnabled,
          ),
        showNavModule: () => Boolean(featureFlags.Configuration),
        isLocked: () => !lockedSubModule.TransactionApprovalWorkflowEnabled,
        hasNotification: approvalInformation.hasInvalidWorkflows,
        NotificationType: 'icon' as const,
      },
    ].filter((subModule) => subModule.showNavModule()),
    subAccounts: [
      {
        id: 'navModule-createSubAccount',
        icon: 'createSubAccount' as const,
        activeIcon: 'createSubAccountOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('createSubAccount.label'),
        isActive: activeModule === 'createSubAccount',
        path: '/create-sub-account',
        title: t('createSubAccount.title'),
        activeModuleId: 'createSubAccount',
        subNavModules: [],
        isOpen: false,
        onClick: () =>
          callback(
            'createSubAccount',
            '/create-sub-account',
            t('createSubAccount.title'),
            !lockedSubModule.SubAccountPermissions,
          ),
        showNavModule: () => Boolean(featureFlags.ViewSubaccounts),
        isLocked: () => !lockedSubModule.SubAccountPermissions,
      },
      {
        id: 'navModule-subAccountsList',
        icon: 'subAccountsList' as const,
        activeIcon: 'subAccountsListOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('subAccountsList.label'),
        isActive: activeModule === 'subAccountsList',
        path: '/sub-accounts-list',
        title: t('subAccountsList.title'),
        activeModuleId: 'subAccountsList',
        subNavModules: [],
        isOpen: false,
        onClick: () => callback('subAccountsList', '/sub-accounts-list', t('subAccountsList.title')),
        showNavModule: () => Boolean(featureFlags.ViewSubaccounts),
        isLocked: () => !lockedSubModule.SubAccountPermissions,
      },
      {
        id: 'navModule-accountTransfer',
        icon: 'accountTransfer' as const,
        activeIcon: 'accountTransferOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('accountTransfer.label'),
        isActive: activeModule === 'accountTransfer',
        path: '/sub-account-transfer',
        title: t('accountTransfer.title'),
        activeModuleId: 'accountTransfer',
        isOpen: false,
        subNavModules: [],
        onClick: () =>
          callback(
            'accountTransfer',
            '/sub-account-transfer',
            t('accountTransfer.title'),
            !lockedSubModule.AccountTransferEnabled,
          ),
        showNavModule: () => Boolean(featureFlags.CreateAccountTransfer),
        isLocked: () => !lockedSubModule.AccountTransferEnabled,
      },
    ].filter((subModule) => subModule.showNavModule()),
    clientAccounts: [
      {
        id: 'navModule-addNewClientAccount',
        icon: 'addNewClientAccount' as const,
        activeIcon: 'addNewClientAccountOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('addNewClientAccount.label'),
        isActive: activeModule === 'addNewClientAccount',
        path: '/add-client-account',
        title: t('addNewClientAccount.title'),
        activeModuleId: 'addNewClientAccount',
        isOpen: false,
        clientNavModules: [],
        onClick: () => callback('addNewClientAccount', '/add-client-account', t('addNewClientAccount.title')),
        showNavModule: () => Boolean(featureFlags.AddClientAccount),
        isLocked: () => false,
      },
      {
        id: 'navModule-clientAccountsList',
        icon: 'clientAccountList' as const,
        activeIcon: 'clientAccountListOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('clientAccountsList.label'),
        isActive: activeModule === 'clientAccountsList',
        path: '/client-accounts',
        title: t('clientAccountsList.title'),
        activeModuleId: 'clientAccountsList',
        clientNavModules: [],
        isOpen: false,
        onClick: () => callback('clientAccountsList', '/client-accounts', t('clientAccountsList.title')),
        showNavModule: () => Boolean(featureFlags.ViewClientAccounts),
        isLocked: () => false,
      },
      {
        id: 'navModule-clientTransferFunds',
        icon: 'transferFunds' as const,
        activeIcon: 'transferFundsOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('transferFunds.label'),
        isActive: activeModule === 'transferFunds',
        path: '/client-account-transfer-funds',
        title: t('transferFunds.title'),
        activeModuleId: 'transferFunds',
        isOpen: false,
        clientNavModules: [],
        onClick: () => callback('transferFunds', '/client-account-transfer-funds', t('transferFunds.title')),
        showNavModule: () => Boolean(featureFlags.CreateClientAccountTransfers),
        isLocked: () => false,
      },
      {
        id: 'navModule-clientTransferFunds',
        icon: 'scheduledClientAccountTransfers' as const,
        activeIcon: 'scheduledClientAccountTransfersOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('scheduledClientAccountTransfers.label'),
        isActive: activeModule === 'clientAccountSchedule',
        path: '/client-account-schedule',
        title: t('scheduledClientAccountTransfers.title'),
        activeModuleId: 'clientAccountSchedule',
        isOpen: false,
        clientNavModules: [],
        onClick: () =>
          callback('clientAccountSchedule', '/client-account-schedule', t('scheduledClientAccountTransfers.title')),
        showNavModule: () => Boolean(featureFlags.CreateClientAccountTransfers),
      },
    ].filter((subModule) => subModule.showNavModule()),
    eLinx: [
      {
        id: 'navModule-eConnect',
        icon: 'eConnect' as const,
        activeIcon: 'eConnectOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('eConnect.label'),
        isActive: activeModule === 'eConnect',
        path: '/elinx/connect',
        title: t('eConnect.title'),
        activeModuleId: 'eConnect',
        isOpen: false,
        clientNavModules: [],
        onClick: () => callback('eConnect', '/elinx/connect', t('eConnect.title'), !lockedSubModule.PayLinkEnabled),
        showNavModule: () => Boolean(featureFlags.CreateElinxConnect),
        isLocked: () => !lockedSubModule.PayLinkEnabled,
      },
      {
        id: 'navModule-ePay',
        icon: 'ePay' as const,
        activeIcon: 'ePayOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('ePay.label'),
        isActive: activeModule === 'ePay',
        path: '/elinx/pay',
        title: t('ePay.title'),
        activeModuleId: 'ePay',
        isOpen: false,
        clientNavModules: [],
        onClick: () =>
          callback(
            'ePay',
            '/elinx/pay',
            t('ePay.title'),
            !lockedSubModule.PaymentsEnabled || !lockedSubModule.PayLinkEnabled,
          ),
        showNavModule: () => Boolean(featureFlags.CreateElinxPay),
        isLocked: () => !lockedSubModule.PaymentsEnabled || !lockedSubModule.PayLinkEnabled,
      },
      {
        id: 'navModule-eCollect',
        icon: 'eCollect' as const,
        activeIcon: 'eCollectOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('eCollect.label'),
        isActive: activeModule === 'eCollect',
        path: '/elinx/collect',
        title: t('eCollect.title'),
        activeModuleId: 'eCollect',
        isOpen: false,
        clientNavModules: [],
        onClick: () =>
          callback(
            'eCollect',
            '/elinx/collect',
            t('eCollect.title'),
            !lockedSubModule.CollectionsEnabled || !lockedSubModule.PayLinkEnabled,
          ),
        showNavModule: () => Boolean(featureFlags.CreateElinxCollect),
        isLocked: () => !lockedSubModule.CollectionsEnabled || !lockedSubModule.PayLinkEnabled,
      },
      {
        id: 'navModule-eFlex',
        icon: 'eFlexOn' as const,
        activeIcon: 'eFlex' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('eFlex.label'),
        isActive: activeModule === 'eFlex',
        path: '/elinx/pay',
        title: t('eFlex.title'),
        activeModuleId: 'eFlex',
        isOpen: false,
        clientNavModules: [],
        onClick: () =>
          callback(
            'eFlex',
            '/elinx/flex',
            t('eFlex.title'),
            !lockedSubModule.CollectionsEnabled || !lockedSubModule.PayLinkEnabled,
          ),
        showNavModule: () => Boolean(featureFlags.CreateElinxFlex),
        isLocked: () => !lockedSubModule.CollectionsEnabled || !lockedSubModule.PayLinkEnabled,
      },
      {
        id: 'navModule-elinx-history',
        icon: 'eLinxHistory' as const,
        activeIcon: 'eLinxHistoryOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('paylinkHistory.label'),
        isActive: activeModule === 'elinxHistory',
        isOpen: false,
        path: '/elinx-history',
        title: t('paylinkHistory.title'),
        activeModuleId: 'elinxHistory',
        subNavModules: [],
        onClick: () =>
          callback('elinxHistory', '/elinx-history', t('paylinkHistory.title'), !lockedSubModule.PayLinkEnabled),
        showNavModule: () => Boolean(featureFlags.ViewPaylinkHitory),
        isLocked: () => !lockedSubModule.PayLinkEnabled,
      },
    ].filter((subModule) => subModule.showNavModule()),
    payment: [
      {
        id: 'navModule-singlePayment',
        icon: 'payment' as const,
        activeIcon: 'paymentOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('singlePayment.label'),
        isActive: activeModule === 'singlePayment',
        subNavModules: [],
        isOpen: false,
        path: '/single-payment',
        title: t('singlePayment.title'),
        activeModuleId: 'singlePayment',
        onClick: () =>
          callback('singlePayment', '/single-payment', t('singlePayment.title'), !lockedSubModule.PaymentsEnabled),
        showNavModule: () => Boolean(featureFlags.CreatePayments),
        isLocked: () => !lockedSubModule.PaymentsEnabled,
      },
      {
        id: 'navModule-bulkPayment',
        icon: 'bulkPayment' as const,
        activeIcon: 'bulkPaymentOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('bulkPayment.label'),
        isActive: activeModule === 'bulkPayment',
        isOpen: false,
        subNavModules: [],
        path: '/bulk-payment',
        title: t('bulkPayment.title'),
        activeModuleId: 'bulkPayment',
        onClick: () =>
          callback('bulkPayment', '/bulk-payment', t('bulkPayment.title'), !lockedSubModule.PaymentsEnabled),
        showNavModule: () => Boolean(featureFlags.BulkPayments),
        isLocked: () => !lockedSubModule.PaymentsEnabled,
      },
      {
        id: 'navModule-billPayment',
        icon: 'payables' as const,
        activeIcon: 'payablesOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('billPayment.label'),
        isActive: activeModule === 'billPayment',
        isOpen: false,
        subNavModules: [],
        path: '/bill-payment',
        title: t('billPayment.title'),
        activeModuleId: 'billPayment',

        onClick: () =>
          callback('billPayment', '/bill-payment', t('billPayment.title'), !lockedSubModule.BillPayEnabled),
        showNavModule: () => Boolean(featureFlags.BillPayment),
        isLocked: () => !lockedSubModule.BillPayEnabled,
      },
    ].filter((subModule) => subModule.showNavModule()),
    collection: [
      {
        id: 'navModule-singleCollection',
        icon: 'collection' as const,
        activeIcon: 'collectionOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('singleCollection.label'),
        isActive: activeModule === 'singleCollection',
        subNavModules: [],
        isOpen: false,
        path: '/single-collection',
        title: t('singleCollection.title'),
        activeModuleId: 'singleCollection',
        onClick: () =>
          callback(
            'singleCollection',
            '/single-collection',
            t('singleCollection.title'),
            !lockedSubModule.CollectionsEnabled,
          ),
        showNavModule: () => Boolean(featureFlags.CreateCollections),
        isLocked: () => !lockedSubModule.CollectionsEnabled,
      },
      {
        id: 'navModule-bulkCollection',
        icon: 'bulkCollection' as const,
        activeIcon: 'bulkCollectionOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('bulkCollection.label'),
        isActive: activeModule === 'bulkCollection',
        isOpen: false,
        path: '/bulk-collection',
        title: t('bulkCollection.title'),
        activeModuleId: 'bulkCollection',
        subNavModules: [],
        onClick: () =>
          callback(
            'bulkCollection',
            '/bulk-collection',
            t('bulkCollection.title'),
            !lockedSubModule.CollectionsEnabled,
          ),
        showNavModule: () => Boolean(featureFlags.BulkCollections),
        isLocked: () => !lockedSubModule.CollectionsEnabled,
      },
    ].filter((subModule) => subModule.showNavModule()),
    conversion: [],
    globalSend: [
      {
        id: 'navModule-globalSendHome',
        iconType: 'material' as const,
        materialIcon: 'home' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('globalSendHome.label'),
        isActive: activeModule === 'globalSendHome',
        subNavModules: [],
        isOpen: false,
        path: '/global-send/home',
        title: t('globalSendHome.title'),
        activeModuleId: 'addNewContact',
        onClick: () => callback('globalSendHome', '/global-send/home', t('globalSendHome.title')),
        showNavModule: () => true,
        isLocked: () => false,
      },
      {
        id: 'navModule-globalSendSend',
        iconType: 'material' as const,
        materialIcon: 'send' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('globalSendSend.label'),
        isActive: activeModule === 'globalSendSend',
        subNavModules: [],
        isOpen: false,
        path: '/global-send/send',
        title: t('globalSendSend.title'),
        activeModuleId: 'globalSendSend',
        onClick: () => callback('addNewContact', '/global-send/send', t('globalSendSend.title')),
        showNavModule: () => true,
        isLocked: () => false,
      },
      {
        id: 'navModule-globalSendRecipients',
        iconType: 'material' as const,
        materialIcon: 'group' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('globalSendRecipients.label'),
        isActive: activeModule === 'globalSendRecipients',
        subNavModules: [],
        isOpen: false,
        path: '/global-send/recipients',
        title: t('globalSendRecipients.title'),
        activeModuleId: 'globalSendRecipients',
        onClick: () => callback('globalSendRecipients', '/global-send/recipients', t('globalSendRecipients.title')),
        showNavModule: () => true,
        isLocked: () => false,
      },
      {
        id: 'navModule-globalSendActivity',
        iconType: 'material' as const,
        materialIcon: 'list_alt' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('globalSendActivity.label'),
        isActive: activeModule === 'globalSendActivity',
        subNavModules: [],
        isOpen: false,
        path: '/global-send/activity',
        title: t('globalSendActivity.title'),
        activeModuleId: 'globalSendActivity',
        onClick: () => callback('globalSendActivity', '/global-send/activity', t('globalSendActivity.title')),
        showNavModule: () => true,
        isLocked: () => false,
      },
      {
        id: 'navModule-globalSendFxEstimator',
        iconType: 'material' as const,
        materialIcon: 'timeline' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('globalSendFxEstimator.label'),
        isActive: activeModule === 'globalSendFxEstimator',
        subNavModules: [],
        isOpen: false,
        path: '/global-send/fxEstimator',
        title: t('globalSendFxEstimator.title'),
        activeModuleId: 'globalSendFxEstimator',
        onClick: () => callback('globalSendFxEstimator', '/global-send/fxEstimator', t('globalSendFxEstimator.title')),
        showNavModule: () => true,
        isLocked: () => false,
      },
    ].filter((subModule) => subModule.showNavModule()),
    disputeManagement: [],
    contacts: [
      {
        id: 'navModule-addNewContact',
        icon: 'addContact' as const,
        activeIcon: 'addContactOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('addNewContact.label'),
        isActive: activeModule === 'addNewContact',
        subNavModules: [],
        isOpen: false,
        path: '/contacts/create',
        title: t('addNewContact.title'),
        activeModuleId: 'addNewContact',
        onClick: () => callback('addNewContact', '/contacts/create', t('addNewContact.title')),
        showNavModule: () => Boolean(featureFlags.AddBeneficiaries),
        isLocked: () => false,
      },
      {
        id: 'navModule-contactAccounts',
        icon: 'clientAccountList' as const,
        activeIcon: 'clientAccountListOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('contactAccounts.label'),
        isActive: activeModule === 'contactAccounts',
        subNavModules: [],
        isOpen: false,
        path: '/contacts',
        title: t('contactAccounts.title'),
        activeModuleId: 'contactAccounts',
        onClick: () => callback('contactAccounts', '/contacts', t('contactAccounts.title')),
        showNavModule: () => Boolean(featureFlags.ViewBeneficiaries),
        isLocked: () => false,
      },
      {
        id: 'navModule-ContactFileUpload',
        icon: 'contactsUpload' as const,
        activeIcon: 'contactsUploadOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('ContactFileUpload.label'),
        isActive: activeModule === 'ContactFileUpload',
        isOpen: false,
        subNavModules: [],
        path: '/contact-file-upload',
        title: t('ContactFileUpload.title'),
        activeModuleId: 'ContactFileUpload',
        onClick: () => callback('ContactFileUpload', '/contact-file-upload', t('ContactFileUpload.title')),
        showNavModule: () => Boolean(featureFlags.AddBeneficiaries),
        isLocked: () => false,
      },
      {
        id: 'navModule-ContactDownloadTemplate',
        icon: 'contactsDownload' as const,
        activeIcon: 'contactsDownloadOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('ContactDownloadTemplate.label'),
        isActive: activeModule === 'ContactdownloadTemplate',
        isOpen: false,
        subNavModules: [],
        path: '/contact-download-template',
        title: t('ContactDownloadTemplate.title'),
        activeModuleId: 'ContactdownloadTemplate',
        onClick: () =>
          callback('ContactDownloadTemplate', '/contact-download-template', t('ContactDownloadTemplate.title')),
        showNavModule: () => Boolean(featureFlags.AddBeneficiaries),
        isLocked: () => false,
      },
    ].filter((subModule) => subModule.showNavModule()),
    upload: [],
    software: [
      {
        id: 'navModule-receivables-software',
        icon: 'receivables' as const,
        activeIcon: 'receivablesOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('receivables.label'),
        isActive: activeModule === 'receivables',
        subNavModules: [],
        isOpen: false,
        path: '/receivables',
        title: t('receivables.title'),
        activeModuleId: 'receivables',
        onClick: () => callback('receivables', '/receivables', t('receivables.title')),
        showNavModule: () => true,
        isLocked: () => false,
      },
      {
        id: 'navModule-payables-software',
        icon: 'payables' as const,
        activeIcon: 'payablesOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('payables.label'),
        isActive: activeModule === 'payables',
        isOpen: false,
        subNavModules: [],
        path: '/payables',
        title: t('payables.title'),
        activeModuleId: 'payables',
        onClick: () => callback('payables', '/payables', t('payables.title')),
        showNavModule: () => true,
        isLocked: () => false,
      },
    ].filter((subModule) => subModule.showNavModule()),
    transactions: [],
    statements: [
      {
        id: 'navModule-transactions-history',
        icon: 'transactionsHistory' as const,
        activeIcon: 'transactionsHistoryOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('transactionsHistory.label'),
        isActive: activeModule === 'transactionsHistory',
        subNavModules: [],
        isOpen: false,
        path: '/transactions-history',
        title: t('transactionsHistory.title'),
        activeModuleId: 'transactionsHistory',
        onClick: () => callback('transactionsHistory', '/transactions-history', t('transactionsHistory.title')),
        showNavModule: () => Boolean(featureFlags.ViewTransactionHistory),
        isLocked: () => false,
      },
      {
        id: 'navModule-bank-verify-history',
        icon: 'bankVerifyHistory' as const,
        activeIcon: 'bankVerifyHistoryOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('bankVerifyHistory.label'),
        isActive: activeModule === 'bankVerifyHistory',
        isOpen: false,
        path: '/bank-verify-history',
        title: t('bankVerifyHistory.title'),
        activeModuleId: 'bankVerifyHistory',
        subNavModules: [],
        onClick: () => callback('bankVerifyHistory', '/bank-verify-history', t('bankVerifyHistory.title')),
        showNavModule: () => Boolean(featureFlags.ViewBankVerify),
        isLocked: () => false,
      },
      {
        id: 'navModule-reports',
        icon: 'reports' as const,
        activeIcon: 'reportsOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('reports.label'),
        isActive: activeModule === 'reports',
        isOpen: false,
        path: '/reports',
        title: t('reports.title'),
        activeModuleId: 'reports',
        subNavModules: [],
        onClick: () => callback('reports', '/reports', t('reports.title')),
        showNavModule: () => Boolean(featureFlags.ViewTransactionReport),
      },
      {
        id: 'navModule-reconciliation',
        icon: 'paylink' as const,
        activeIcon: 'paylinkOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('reconciliation.label'),
        isActive: activeModule === 'reconciliation',
        isOpen: false,
        path: '/reconciliation',
        title: t('reconciliation.title'),
        activeModuleId: 'reconciliation',
        subNavModules: [],
        onClick: () => callback('reconciliation', '/reconciliation', t('reconciliation.title')),
        showNavModule: () => AccountType === 'Technology',
        isLocked: () => false,
      },
    ].filter((subModule) => subModule.showNavModule()),
    integrations: [],
    settings: [],
    interestAccount: [],
    support: [
      {
        id: 'navModule-ticketsList',
        icon: 'ticketList' as const,
        activeIcon: 'ticketListOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('ticketsList.label'),
        isActive: activeModule === 'ticketsList',
        path: '/tickets',
        title: t('ticketsList.title'),
        activeModuleId: 'ticketsList',
        subNavModules: [],
        isOpen: false,
        onClick: () => callback('ticketsList', '/tickets', t('ticketsList.title')),
        showNavModule: () => Boolean(featureFlags.ViewSupportTickets),
        isLocked: () => false,
      },
      {
        id: 'navModule-learningCentre',
        icon: 'learningCenter' as const,
        activeIcon: 'learningCenterOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: getLuminance(secondaryColor) > 0.5 ? ('primary-dark' as const) : ('primary-light' as const),
        label: t('learningCentre.label'),
        isActive: activeModule === 'learningCentre',
        path: '/learning-centre',
        title: t('learningCentre.title'),
        activeModuleId: 'learningCentre',
        subNavModules: [],
        isOpen: false,
        onClick: () => callback('learningCentre', '/learning-centre', t('learningCentre.title')),
        showNavModule: () => Boolean(featureFlags.ViewLearningCentre),
      },
    ].filter((subModule) => subModule.showNavModule()),
  };

  return handlers[parentModule];
}

function getLabelAndDescription(parentModule: string) {
  const handlers: { [key: string]: string[] } = {
    dashboard: [],
    myAccount: [],
    transactionApproval: [],
    subAccounts: [],
    clientAccounts: [],
    eLinx: [],
    payment: [],
    collection: [],
    conversion: [],
    globalSend: [],
    contacts: [],
    upload: [],
    software: [],
    transactions: [],
    statements: [],
    integrations: [],
    settings: [],
    support: [],
    interestAccount: [],
    disputeManagement: [],
  };

  return handlers[parentModule];
}
